<template>
  <v-card-text>
    <!-- Filtros y búsqueda -->
    <v-row class="mb-4">
      <v-col cols="12" md="6">
        <v-text-field
          v-model="searchQuery"
          prepend-inner-icon="mdi-magnify"
          label="Buscar incidencias..."
          variant="outlined"
          density="compact"
          clearable
          @input="applyFilters"
        />
      </v-col>

      <v-col cols="12" md="2">
        <v-select
          v-model="statusFilter"
          :items="statusOptions"
          label="Estado"
          variant="outlined"
          density="compact"
          clearable
          @update:model-value="applyFilters"
        />
      </v-col>

      <v-col cols="12" md="2">
        <v-select
          v-model="priorityFilter"
          :items="priorityOptions"
          label="Prioridad"
          variant="outlined"
          density="compact"
          clearable
          @update:model-value="applyFilters"
        />
      </v-col>

      <v-col cols="12" md="2">
        <v-btn
          @click="refreshData"
          :loading="loading"
          color="primary"
          variant="outlined"
          block
        >
          <v-icon left>mdi-refresh</v-icon>
          Actualizar
        </v-btn>
      </v-col>
    </v-row>

    <!-- Filtros de fecha -->
    <v-row class="mb-4">
      <v-col cols="12" md="4">
        <v-text-field
          v-model="dateFrom"
          label="Desde"
          type="date"
          variant="outlined"
          density="compact"
          @change="applyFilters"
        />
      </v-col>

      <v-col cols="12" md="4">
        <v-text-field
          v-model="dateTo"
          label="Hasta"
          type="date"
          variant="outlined"
          density="compact"
          @change="applyFilters"
        />
      </v-col>

      <v-col cols="12" md="4" class="d-flex align-center">
        <v-btn-toggle v-model="timeRange" mandatory>
          <v-btn value="week" size="small">Semana</v-btn>
          <v-btn value="month" size="small">Mes</v-btn>
          <v-btn value="quarter" size="small">Trimestre</v-btn>
          <v-btn value="all" size="small">Todo</v-btn>
        </v-btn-toggle>
      </v-col>
    </v-row>

    <!-- Lista de incidencias -->
    <v-row v-if="loading">
      <v-col cols="12">
        <div class="text-center py-8">
          <v-progress-circular indeterminate color="primary" size="64"></v-progress-circular>
          <div class="mt-4 text-h6">Cargando historial...</div>
        </div>
      </v-col>
    </v-row>

    <v-row v-else-if="filteredIncidents.length === 0">
      <v-col cols="12">
        <v-alert type="info" prominent>
          <v-icon slot="prepend">mdi-information</v-icon>
          <div class="text-h6">No hay incidencias</div>
          <div>No se encontraron incidencias que coincidan con los filtros seleccionados.</div>
        </v-alert>
      </v-col>
    </v-row>

    <v-row v-else>
      <v-col
        v-for="incident in paginatedIncidents"
        :key="incident.id"
        cols="12"
        md="6"
        lg="4"
      >
        <v-card
          class="incident-card"
          :class="getIncidentCardClass(incident)"
          @click="openIncidentDetails(incident)"
        >
          <v-card-title class="d-flex align-center">
            <v-icon
              :color="getPriorityColor(incident.priority)"
              class="me-2"
            >
              {{ getPriorityIcon(incident.priority) }}
            </v-icon>
            <span class="text-truncate">{{ incident.title }}</span>
            <v-spacer></v-spacer>
            <v-chip
              :color="getStatusColor(incident.status)"
              size="small"
              label
            >
              {{ getStatusText(incident.status) }}
            </v-chip>
          </v-card-title>

          <v-card-subtitle>
            <div class="d-flex align-center mb-1">
              <v-icon size="16" class="me-1">mdi-clock</v-icon>
              {{ formatDate(incident.createdAt) }}
            </div>
            <div class="d-flex align-center">
              <v-icon size="16" class="me-1">mdi-map-marker</v-icon>
              <span class="text-truncate">{{ incident.point?.address || incident.point?.name || 'Sin dirección' }}</span>
            </div>
          </v-card-subtitle>

          <v-card-text>
            <p class="text-body-2 text-truncate mb-2">{{ incident.description }}</p>
            
            <div class="d-flex align-center justify-space-between">
              <v-chip
                :color="getCategoryColor(incident.category)"
                size="small"
                variant="outlined"
              >
                {{ incident.category || 'Sin categoría' }}
              </v-chip>
              
              <div class="text-caption text-grey">
                ID: #{{ incident.id }}
              </div>
            </div>
          </v-card-text>

          <v-card-actions>
            <v-btn
              size="small"
              variant="text"
              @click.stop="openIncidentDetails(incident)"
            >
              <v-icon left size="16">mdi-eye</v-icon>
              Ver Detalles
            </v-btn>

            <v-spacer></v-spacer>

            <v-btn
              v-if="incident.status?.toLowerCase() !== 'resolved'"
              size="small"
              variant="text"
              color="primary"
              @click.stop="addComment(incident)"
            >
              <v-icon left size="16">mdi-comment-plus</v-icon>
              Comentar
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-col>
    </v-row>

    <!-- Paginación -->
    <v-row v-if="filteredIncidents.length > itemsPerPage" class="mt-4">
      <v-col cols="12">
        <v-pagination
          v-model="currentPage"
          :length="totalPages"
          :total-visible="7"
        />
      </v-col>
    </v-row>

    <!-- Dialog de detalles - Diseño profesional -->
    <v-dialog 
      v-model="showDetailsDialog" 
      max-width="900px"
      scrollable
      @click:outside="showDetailsDialog = false"
      @keydown.esc="showDetailsDialog = false"
    >
      <v-card v-if="selectedIncident" class="incident-details-dialog">
        <!-- Header mejorado -->
        <v-card-title class="d-flex align-center pa-4">
          <v-icon
            :icon="getPriorityIcon(selectedIncident.priority)"
            :color="getPriorityColor(selectedIncident.priority)"
            size="28"
            class="mr-3"
          ></v-icon>
          <div class="flex-grow-1">
            <h2 class="text-h6 mb-1">
              Detalles de Incidencia #{{ selectedIncident.id }}
            </h2>
            <p class="text-caption text-grey mb-0">
              {{ selectedIncident.title }}
            </p>
          </div>
          <v-chip
            :color="getStatusColor(selectedIncident.status)"
            variant="tonal"
            class="mr-3"
          >
            <v-icon 
              :icon="getStatusIcon(selectedIncident.status)" 
              size="16" 
              class="mr-1"
            ></v-icon>
            {{ getStatusText(selectedIncident.status) }}
          </v-chip>
          <v-btn
            icon="mdi-close"
            variant="text"
            size="small"
            @click="showDetailsDialog = false"
          ></v-btn>
        </v-card-title>

        <!-- Contenido organizado -->
        <v-card-text class="pa-0">
          <v-container class="py-6">
            <v-row>
              <!-- Columna principal -->
              <v-col cols="12" md="8">
                <!-- Descripción -->
                <v-card flat class="mb-4" elevation="1">
                  <v-card-title class="text-h6 pb-2">
                    <v-icon icon="mdi-text-long" class="mr-2"></v-icon>
                    Descripción
                  </v-card-title>
                  <v-card-text>
                    <p class="text-body-1">{{ selectedIncident.description }}</p>
                  </v-card-text>
                </v-card>

                <!-- Ubicación -->
                <v-card v-if="selectedIncident.point" flat class="mb-4" elevation="1">
                  <v-card-title class="text-h6 pb-2">
                    <v-icon icon="mdi-map-marker" class="mr-2"></v-icon>
                    Ubicación
                  </v-card-title>
                  <v-card-text>
                    <div class="mb-2">
                      <strong>{{ selectedIncident.point.name }}</strong>
                    </div>
                    <div class="text-body-2 text-grey">
                      {{ selectedIncident.point.address || 'Sin dirección especificada' }}
                    </div>
                  </v-card-text>
                </v-card>

                <!-- Comentarios -->
                <v-card flat class="mb-4" elevation="1">
                  <v-card-title class="text-h6 pb-2 d-flex justify-space-between align-center">
                    <div>
                      <v-icon icon="mdi-comment-multiple" class="mr-2"></v-icon>
                      Comentarios ({{ selectedIncident.comments?.filter(c => !c.isDeleted).length || 0 }})
                    </div>
                    <v-btn
                      color="primary"
                      variant="outlined"
                      size="small"
                      prepend-icon="mdi-plus"
                      @click="addComment(selectedIncident)"
                    >
                      Agregar
                    </v-btn>
                  </v-card-title>
                  <v-card-text>
                    <!-- Banner de respuesta/edición -->
                    <v-alert
                      v-if="replyingTo || editingComment"
                      :type="editingComment ? 'info' : 'success'"
                      variant="tonal"
                      closable
                      class="mb-3"
                      @click:close="cancelCommentAction"
                    >
                      <div class="d-flex align-center">
                        <v-icon 
                          :icon="editingComment ? 'mdi-pencil' : 'mdi-reply'" 
                          class="mr-2"
                        ></v-icon>
                        <div>
                          <div class="text-subtitle-2">
                            {{ editingComment ? 'Editando comentario' : `Respondiendo a ${replyingTo?.userName}` }}
                          </div>
                          <div class="text-caption text-grey">
                            {{ (editingComment ? editingComment.comment : replyingTo?.comment || '').substring(0, 80) }}...
                          </div>
                        </div>
                      </div>
                    </v-alert>

                    <div v-if="!selectedIncident.comments || selectedIncident.comments.length === 0" 
                         class="text-center text-grey py-4">
                      No hay comentarios aún
                    </div>
                    <div v-else class="comments-list">
                      <v-card
                        v-for="comment in selectedIncident.comments"
                        :key="comment.id"
                        variant="outlined"
                        :class="['mb-3', { 'deleted-comment': comment.isDeleted }]"
                      >
                        <v-card-text class="pa-3">
                          <!-- Preview de respuesta -->
                          <div v-if="comment.replyTo" class="reply-to-preview mb-2 pa-2 rounded" style="background: rgba(0,0,0,0.05)">
                            <v-icon icon="mdi-reply" size="14" class="mr-1"></v-icon>
                            <span class="text-caption">
                              <strong>{{ comment.replyTo.userName }}</strong>: 
                              {{ comment.replyTo.comment }}
                            </span>
                          </div>

                          <div class="d-flex justify-space-between align-center mb-2">
                            <div class="d-flex align-center">
                              <v-avatar size="24" color="primary" class="mr-2">
                                <v-icon :icon="comment.isDeleted ? 'mdi-account-off' : 'mdi-account'" size="14"></v-icon>
                              </v-avatar>
                              <span class="font-weight-medium">{{ comment.userName || 'Usuario' }}</span>
                            </div>
                            <div class="d-flex align-center">
                              <span class="text-caption text-grey mr-2">
                                {{ formatDate(comment.createdAt || comment.created_at || comment.timestamp) }}
                                <span v-if="comment.isEdited && !comment.isDeleted" class="ml-1">(editado)</span>
                              </span>
                              <!-- Menú de acciones -->
                              <v-menu v-if="canModifyComment(comment)">
                                <template v-slot:activator="{ props }">
                                  <v-btn
                                    icon="mdi-dots-vertical"
                                    variant="text"
                                    size="x-small"
                                    v-bind="props"
                                  ></v-btn>
                                </template>
                                <v-list>
                                  <v-list-item 
                                    v-if="!comment.isDeleted && canEditComment(comment)"
                                    @click="startEditComment(comment)"
                                  >
                                    <template v-slot:prepend>
                                      <v-icon icon="mdi-pencil" color="primary"></v-icon>
                                    </template>
                                    <v-list-item-title>Editar</v-list-item-title>
                                  </v-list-item>
                                  <v-list-item 
                                    v-if="!comment.isDeleted"
                                    @click="startReplyComment(comment)"
                                  >
                                    <template v-slot:prepend>
                                      <v-icon icon="mdi-reply" color="info"></v-icon>
                                    </template>
                                    <v-list-item-title>Responder</v-list-item-title>
                                  </v-list-item>
                                  <v-list-item 
                                    v-if="canDeleteComment(comment)"
                                    @click="deleteComment(comment.id)"
                                  >
                                    <template v-slot:prepend>
                                      <v-icon icon="mdi-delete" color="error"></v-icon>
                                    </template>
                                    <v-list-item-title>Eliminar</v-list-item-title>
                                  </v-list-item>
                                </v-list>
                              </v-menu>
                            </div>
                          </div>
                          <p :class="['mb-0 text-body-2', { 'text-grey': comment.isDeleted }]">
                            {{ comment.comment }}
                          </p>
                        </v-card-text>
                      </v-card>
                    </div>
                  </v-card-text>
                </v-card>
              </v-col>

              <!-- Sidebar derecho -->
              <v-col cols="12" md="4">
                <!-- Información general -->
                <v-card flat class="mb-4" elevation="1">
                  <v-card-title class="text-h6 pb-2">
                    <v-icon icon="mdi-information" class="mr-2"></v-icon>
                    Información General
                  </v-card-title>
                  <v-card-text>
                    <div class="info-list">
                      <div class="info-item mb-3">
                        <div class="text-caption text-grey mb-1">Prioridad</div>
                        <v-chip
                          :color="getPriorityColor(selectedIncident.priority)"
                          size="small"
                          variant="tonal"
                        >
                          <v-icon 
                            :icon="getPriorityIcon(selectedIncident.priority)" 
                            size="14" 
                            class="mr-1"
                          ></v-icon>
                          {{ getPriorityText(selectedIncident.priority) }}
                        </v-chip>
                      </div>
                      
                      <div class="info-item mb-3">
                        <div class="text-caption text-grey mb-1">Categoría</div>
                        <v-chip
                          :color="getCategoryColor(selectedIncident.category)"
                          size="small"
                          variant="tonal"
                        >
                          {{ selectedIncident.category || 'Sin categoría' }}
                        </v-chip>
                      </div>

                      <div class="info-item mb-3">
                        <div class="text-caption text-grey mb-1">Fecha de Reporte</div>
                        <div class="text-body-2">
                          <v-icon icon="mdi-calendar" size="16" class="mr-1"></v-icon>
                          {{ formatDate(selectedIncident.createdAt) }}
                        </div>
                      </div>

                      <div class="info-item mb-3">
                        <div class="text-caption text-grey mb-1">Última Actualización</div>
                        <div class="text-body-2">
                          <v-icon icon="mdi-update" size="16" class="mr-1"></v-icon>
                          {{ formatDate(selectedIncident.updatedAt) }}
                        </div>
                      </div>

                      <div class="info-item mb-3">
                        <div class="text-caption text-grey mb-1">Tiempo Transcurrido</div>
                        <div class="text-body-2">
                          <v-icon icon="mdi-clock-outline" size="16" class="mr-1"></v-icon>
                          {{ getElapsedTime(selectedIncident.createdAt) }}
                        </div>
                      </div>

                      <div class="info-item">
                        <div class="text-caption text-grey mb-1">ID de Incidencia</div>
                        <div class="text-body-2 font-weight-medium">
                          #{{ selectedIncident.id }}
                        </div>
                      </div>
                    </div>
                  </v-card-text>
                </v-card>

                <!-- Asignación -->
                <v-card flat elevation="1">
                  <v-card-title class="text-h6 pb-2">
                    <v-icon icon="mdi-account-tie" class="mr-2"></v-icon>
                    Asignación
                  </v-card-title>
                  <v-card-text>
                    <div class="text-body-2">
                      {{ selectedIncident.assignedTo ? `Asignado a: ${selectedIncident.assignedTo}` : 'Sin asignar' }}
                    </div>
                  </v-card-text>
                </v-card>
              </v-col>
            </v-row>
          </v-container>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Diálogo de confirmación de eliminación -->
    <v-dialog v-model="showDeleteDialog" max-width="400px">
      <v-card>
        <v-card-title class="text-h6 d-flex align-center">
          <v-icon icon="mdi-alert-circle-outline" color="error" class="mr-2"></v-icon>
          Confirmar Eliminación
        </v-card-title>
        <v-card-text>
          <p class="text-body-1 mb-0">
            ¿Estás seguro de que deseas eliminar este comentario?
          </p>
          <p class="text-caption text-grey mt-2">
            Esta acción no se puede deshacer. El comentario se mostrará como "Se eliminó este mensaje".
          </p>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn 
            variant="text" 
            @click="showDeleteDialog = false; commentToDelete = null"
          >
            Cancelar
          </v-btn>
          <v-btn 
            color="error" 
            variant="flat"
            :loading="deletingComment"
            @click="confirmDeleteComment"
          >
            Eliminar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Dialog para añadir/editar/responder comentario -->
    <v-dialog v-model="showCommentDialog" max-width="500px">
      <v-card v-if="selectedIncident">
        <v-card-title>
          {{ editingComment ? 'Editar Comentario' : replyingTo ? 'Responder Comentario' : 'Añadir Comentario' }}
        </v-card-title>
        <v-card-text>
          <!-- Banner de respuesta -->
          <v-alert
            v-if="replyingTo"
            type="success"
            variant="tonal"
            closable
            class="mb-3"
            @click:close="cancelCommentAction"
          >
            <div class="d-flex align-center">
              <v-icon icon="mdi-reply" class="mr-2"></v-icon>
              <div>
                <div class="text-subtitle-2">Respondiendo a {{ replyingTo.userName }}</div>
                <div class="text-caption text-grey">
                  {{ replyingTo.comment.substring(0, 80) }}...
                </div>
              </div>
            </div>
          </v-alert>

          <!-- Banner de edición -->
          <v-alert
            v-if="editingComment"
            type="info"
            variant="tonal"
            closable
            class="mb-3"
            @click:close="cancelCommentAction"
          >
            <div class="d-flex align-center">
              <v-icon icon="mdi-pencil" class="mr-2"></v-icon>
              <div>
                <div class="text-subtitle-2">Editando comentario</div>
              </div>
            </div>
          </v-alert>

          <v-textarea
            v-model="newComment"
            :label="editingComment ? 'Editar comentario' : 'Comentario'"
            rows="4"
            variant="outlined"
            counter="500"
            :rules="[v => !!v || 'El comentario es requerido', v => v.length <= 500 || 'Máximo 500 caracteres']"
          />
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn @click="showCommentDialog = false; cancelCommentAction()">Cancelar</v-btn>
          <v-btn
            color="primary"
            :loading="submittingComment"
            :disabled="!newComment.trim()"
            @click="submitComment"
          >
            {{ editingComment ? 'Guardar' : 'Enviar' }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-card-text>
</template>

<script setup lang="ts">
import { ref, onMounted, computed, watch } from 'vue'
import SupervisorService from '@/services/supervisorService'
import { useAuthStore } from '@/stores/auth'
// W2 LIFECYCLE-SIBLING (client truth): one shared mirror of the merged backend allowlist.
import { canUseLifecycleSiblingWrites } from '@/config/lifecycleSiblingPerimeter'
import type { Incident } from '@/types'

// ================== PROPS ==================
interface Props {
  supervisorId: number
}

const props = defineProps<Props>()
// W2 LIFECYCLE-SIBLING: identity source for the contained comment edit/delete capability.
const auth = useAuthStore()

// ================== EMITS ==================
const emit = defineEmits<{
  'refresh-stats': []
}>()

// ================== ESTADO REACTIVO ==================
const loading = ref(false)
const submittingComment = ref(false)
const incidents = ref<Incident[]>([])
const filteredIncidents = ref<Incident[]>([])

// Filtros
const searchQuery = ref('')
const statusFilter = ref('')
const priorityFilter = ref('')
const dateFrom = ref('')
const dateTo = ref('')
const timeRange = ref('month')

// Paginación
const currentPage = ref(1)
const itemsPerPage = ref(9)

// Dialogs
const showDetailsDialog = ref(false)
const showCommentDialog = ref(false)
const selectedIncident = ref<Incident | null>(null)
const newComment = ref('')

// Opciones para filtros
const statusOptions = [
  { title: 'Pendiente', value: 'pending' },
  { title: 'En Progreso', value: 'in_progress' },
  { title: 'Resuelta', value: 'resolved' },
  { title: 'Cancelada', value: 'cancelled' }
]

const priorityOptions = [
  { title: 'Crítica', value: 'critical' },
  { title: 'Alta', value: 'high' },
  { title: 'Media', value: 'medium' },
  { title: 'Baja', value: 'low' }
]

// ================== COMPUTED ==================
const totalPages = computed(() => Math.ceil(filteredIncidents.value.length / itemsPerPage.value))

const paginatedIncidents = computed(() => {
  const start = (currentPage.value - 1) * itemsPerPage.value
  const end = start + itemsPerPage.value
  return filteredIncidents.value.slice(start, end)
})

// ================== WATCHERS ==================
watch(timeRange, (newRange) => {
  setDateRange(newRange)
  applyFilters()
})

// ================== MÉTODOS ==================
/**
 * Carga las incidencias del supervisor
 */
const loadIncidents = async () => {
  if (!props.supervisorId) return
  
  try {
    loading.value = true
    const response = await SupervisorService.getMyIncidentsHistory(props.supervisorId, {
      status: statusFilter.value || undefined,
      dateFrom: dateFrom.value || undefined,
      dateTo: dateTo.value || undefined,
      priority: priorityFilter.value || undefined
    })
    incidents.value = response
    applyFilters()
  } catch (error) {
    console.error('Error loading incidents:', error)
  } finally {
    loading.value = false
  }
}

/**
 * Aplica filtros a las incidencias
 */
const applyFilters = () => {
  let filtered = [...incidents.value]

  // Filtro de búsqueda por texto
  if (searchQuery.value) {
    const query = searchQuery.value.toLowerCase()
    filtered = filtered.filter(incident =>
      incident.title.toLowerCase().includes(query) ||
      incident.description.toLowerCase().includes(query) ||
      (incident.category && incident.category.toLowerCase().includes(query))
    )
  }

  // Filtro por rango de fechas
  if (dateFrom.value || dateTo.value) {
    filtered = filtered.filter(incident => {
      const incidentDate = new Date(incident.createdAt)
      const fromDate = dateFrom.value ? new Date(dateFrom.value) : null
      const toDate = dateTo.value ? new Date(dateTo.value + 'T23:59:59') : null
      
      if (fromDate && incidentDate < fromDate) return false
      if (toDate && incidentDate > toDate) return false
      return true
    })
  }

  filteredIncidents.value = filtered
  currentPage.value = 1
}

/**
 * Establece el rango de fechas según la selección
 */
const setDateRange = (range: string) => {
  const now = new Date()
  const today = now.toISOString().split('T')[0]
  
  switch (range) {
    case 'week':
      // 7 días atrás
      const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000)
      dateFrom.value = weekAgo.toISOString().split('T')[0]
      dateTo.value = today
      break
    case 'month':
      // 30 días atrás (más preciso que usar mes calendario)
      const monthAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000)
      dateFrom.value = monthAgo.toISOString().split('T')[0]
      dateTo.value = today
      break
    case 'quarter':
      // 90 días atrás
      const quarterAgo = new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000)
      dateFrom.value = quarterAgo.toISOString().split('T')[0]
      dateTo.value = today
      break
    case 'all':
      dateFrom.value = ''
      dateTo.value = ''
      break
  }
  
  console.log(`📅 Filtro de fecha aplicado: ${range}`, {
    desde: dateFrom.value,
    hasta: dateTo.value
  })
}

/**
 * Actualiza los datos
 */
const refreshData = () => {
  loadIncidents()
  emit('refresh-stats')
}

/**
 * Abre el diálogo de detalles de incidencia
 */
const openIncidentDetails = async (incident: Incident) => {
  try {
    const response = await SupervisorService.getIncidentDetails(props.supervisorId, incident.id)
    selectedIncident.value = response
    showDetailsDialog.value = true
  } catch (error) {
    console.error('Error loading incident details:', error)
  }
}

/**
 * Abre el diálogo para añadir comentario
 */
const addComment = (incident: Incident) => {
  selectedIncident.value = incident
  newComment.value = ''
  showCommentDialog.value = true
}

/**
 * Envía el comentario
 */
const submitComment = async () => {
  if (!selectedIncident.value || !newComment.value.trim()) return
  
  try {
    submittingComment.value = true
    
    // P13-V6B@V1 — ruta canónica de comments (las firmas exigen el incidentId del padre).
    if (editingComment.value) {
      // Editar comentario existente
      // W2 LIFECYCLE-SIBLING: adversarial guard — the dispatcher is reachable without the menu item.
      if (!canUseLifecycleSiblingWrites(auth.userRole)) return
      await SupervisorService.editIncidentComment(
        selectedIncident.value.id,
        editingComment.value.id,
        newComment.value.trim()
      )
    } else {
      // Añadir nuevo comentario o respuesta
      await SupervisorService.addIncidentComment(
        selectedIncident.value.id,
        newComment.value.trim(),
        replyingTo.value?.id
      )
    }
    
    showCommentDialog.value = false
    cancelCommentAction()
    
    // Recargar detalles de la incidencia
    const updatedIncident = await SupervisorService.getIncidentDetails(props.supervisorId, selectedIncident.value.id)
    selectedIncident.value = updatedIncident
    
    loadIncidents()
    emit('refresh-stats')
  } catch (error) {
    console.error('Error adding/editing comment:', error)
  } finally {
    submittingComment.value = false
  }
}

// Estado para respuestas y edición
const replyingTo = ref<any>(null)
const editingComment = ref<any>(null)

/**
 * Inicia el modo de respuesta a un comentario
 */
const startReplyComment = (comment: any) => {
  replyingTo.value = comment
  editingComment.value = null
  newComment.value = ''
  showCommentDialog.value = true
}

/**
 * Inicia el modo de edición de un comentario
 */
const startEditComment = (comment: any) => {
  editingComment.value = comment
  replyingTo.value = null
  newComment.value = comment.comment
  showCommentDialog.value = true
}

/**
 * Cancela la acción de respuesta o edición
 */
const cancelCommentAction = () => {
  replyingTo.value = null
  editingComment.value = null
  newComment.value = ''
}

// Estado para confirmación de eliminación
const showDeleteDialog = ref(false)
const commentToDelete = ref<number | null>(null)
const deletingComment = ref(false)

/**
 * Inicia el proceso de eliminación de comentario
 */
const deleteComment = (commentId: number) => {
  commentToDelete.value = commentId
  showDeleteDialog.value = true
}

/**
 * Confirma y ejecuta la eliminación del comentario
 */
const confirmDeleteComment = async () => {
  // P13-V6B@V1 — la ruta canónica de delete exige el incidentId del padre.
  if (!commentToDelete.value || !selectedIncident.value) return

  try {
    // W2 LIFECYCLE-SIBLING: adversarial guard — the dispatcher is reachable without the menu item.
    if (!canUseLifecycleSiblingWrites(auth.userRole)) return
    deletingComment.value = true
    await SupervisorService.deleteIncidentComment(selectedIncident.value.id, commentToDelete.value)
    
    showDeleteDialog.value = false
    commentToDelete.value = null
    
    // Recargar detalles de la incidencia
    if (selectedIncident.value) {
      const updatedIncident = await SupervisorService.getIncidentDetails(props.supervisorId, selectedIncident.value.id)
      selectedIncident.value = updatedIncident
    }
    
    loadIncidents()
    emit('refresh-stats')
  } catch (error) {
    console.error('Error deleting comment:', error)
    alert('Error al eliminar comentario')
  } finally {
    deletingComment.value = false
  }
}

/**
 * Verifica si el usuario puede editar el comentario
 */
const canEditComment = (comment: any) => {
  // W2 LIFECYCLE-SIBLING: comment EDIT is contained to vendor/conditional roles. This page is
  // admitted to `field_supervisor` only, so the capability is withdrawn for every identity that can
  // mount it; the authorship rule below never gets the chance to re-admit it.
  if (!canUseLifecycleSiblingWrites(auth.userRole)) return false
  // Solo el autor puede editar su propio comentario
  return !comment.isDeleted && comment.userId === props.supervisorId
}

/**
 * Verifica si el usuario puede eliminar el comentario
 */
const canDeleteComment = (comment: any) => {
  // W2 LIFECYCLE-SIBLING: comment DELETE is contained to vendor/conditional roles.
  if (!canUseLifecycleSiblingWrites(auth.userRole)) return false
  if (comment.isDeleted) return false
  // Solo el autor puede eliminar su comentario
  return comment.userId === props.supervisorId
}

/**
 * Verifica si se debe mostrar el menú de acciones
 */
const canModifyComment = (comment: any) => {
  return canDeleteComment(comment) || canEditComment(comment) || !comment.isDeleted
}

/**
 * Obtiene el color según la prioridad
 */
const getPriorityColor = (priority: string) => {
  switch (priority?.toLowerCase()) {
    case 'critical': return 'red'
    case 'high': return 'orange'
    case 'medium': return 'blue'
    case 'low': return 'green'
    default: return 'grey'
  }
}

/**
 * Obtiene el ícono según la prioridad
 */
const getPriorityIcon = (priority: string) => {
  switch (priority?.toLowerCase()) {
    case 'critical': return 'mdi-alert-circle'
    case 'high': return 'mdi-alert'
    case 'medium': return 'mdi-alert-outline'
    case 'low': return 'mdi-information'
    default: return 'mdi-help-circle'
  }
}

/**
 * Obtiene el color según el estado
 */
const getStatusColor = (status: string) => {
  switch (status?.toLowerCase()) {
    case 'pending': return 'warning'
    case 'in_progress': return 'info'
    case 'resolved': return 'success'
    case 'cancelled': return 'grey'
    default: return 'grey'
  }
}

/**
 * Obtiene el texto del estado
 */
const getStatusText = (status: string) => {
  switch (status?.toLowerCase()) {
    case 'pending': return 'Pendiente'
    case 'in_progress': return 'En Progreso'
    case 'resolved': return 'Resuelta'
    case 'cancelled': return 'Cancelada'
    default: return 'Desconocido'
  }
}

/**
 * Obtiene el color de la categoría
 */
const getCategoryColor = (category: string) => {
  if (!category) return 'grey'
  const colors = ['blue', 'purple', 'teal', 'indigo', 'cyan']
  const hash = category.split('').reduce((a, b) => a + b.charCodeAt(0), 0)
  return colors[hash % colors.length]
}

/**
 * Obtiene la clase CSS para la tarjeta de incidencia
 */
const getIncidentCardClass = (incident: Incident) => {
  const baseClass = 'incident-card'
  const priorityClass = `priority-${incident.priority?.toLowerCase()}`
  return `${baseClass} ${priorityClass}`
}

/**
 * Formatea la fecha
 */
const formatDate = (dateString: string | null | undefined) => {
  if (!dateString) {
    console.warn('⚠️ formatDate: fecha vacía o null')
    return 'N/A'
  }
  
  try {
    const date = new Date(dateString)
    if (isNaN(date.getTime())) {
      console.warn('⚠️ formatDate: fecha inválida:', dateString)
      return 'Fecha inválida'
    }
    
    return date.toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  } catch (error) {
    console.error('❌ Error formateando fecha:', error)
    return 'Error en fecha'
  }
}

/**
 * Obtiene el ícono según el estado
 */
const getStatusIcon = (status: string) => {
  switch (status?.toLowerCase()) {
    case 'pending': return 'mdi-clock-outline'
    case 'in_progress': return 'mdi-progress-clock'
    case 'resolved': return 'mdi-check-circle'
    case 'cancelled': return 'mdi-close-circle'
    default: return 'mdi-help-circle'
  }
}

/**
 * Obtiene el texto de la prioridad
 */
const getPriorityText = (priority: string) => {
  switch (priority?.toLowerCase()) {
    case 'critical': return 'Crítica'
    case 'high': return 'Alta'
    case 'medium': return 'Media'
    case 'low': return 'Baja'
    default: return 'Sin definir'
  }
}

/**
 * Calcula el tiempo transcurrido
 */
const getElapsedTime = (dateString: string) => {
  if (!dateString) return 'N/A'
  const now = new Date()
  const date = new Date(dateString)
  const diffMs = now.getTime() - date.getTime()
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))
  const diffHours = Math.floor((diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60))
  
  if (diffDays > 0) {
    return `${diffDays} día${diffDays > 1 ? 's' : ''}`
  } else if (diffHours > 0) {
    return `${diffHours} hora${diffHours > 1 ? 's' : ''}`
  } else {
    const diffMinutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60))
    return `${diffMinutes} minuto${diffMinutes > 1 ? 's' : ''}`
  }
}

// ================== CICLO DE VIDA ==================
onMounted(() => {
  setDateRange(timeRange.value)
  loadIncidents()
})

// ================== EXPOSICIÓN ==================
defineExpose({
  refreshData
})
</script>

<style scoped>
.incident-card {
  transition: all 0.3s ease;
  cursor: pointer;
  border-left: 4px solid transparent;
}

.incident-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.incident-card.priority-critical,
.incident-card.priority-alta {
  border-left-color: #f44336;
}

.incident-card.priority-high,
.incident-card.priority-media {
  border-left-color: #ff9800;
}

.incident-card.priority-medium {
  border-left-color: #2196f3;
}

.incident-card.priority-low,
.incident-card.priority-baja {
  border-left-color: #4caf50;
}

.text-truncate {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.v-btn-toggle {
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 4px;
}

/* Estilos del diálogo de detalles */
.incident-details-dialog .info-list {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

/* Estilos de comentarios */
.deleted-comment {
  opacity: 0.6;
  background-color: rgba(0, 0, 0, 0.02);
}

.reply-to-preview {
  font-style: italic;
  border-left: 3px solid rgba(0, 0, 0, 0.2);
}

.incident-details-dialog .info-item {
  padding: 0.5rem 0;
}

.incident-details-dialog .info-label {
  font-weight: 500;
  color: rgba(0, 0, 0, 0.6);
  font-size: 0.875rem;
}

.incident-details-dialog .comments-list {
  max-height: 400px;
  overflow-y: auto;
}

.incident-details-dialog .comment-item {
  padding: 1rem;
  margin-bottom: 0.75rem;
  background-color: #f5f5f5;
  border-radius: 8px;
}

.incident-details-dialog .comment-header {
  display: flex;
  justify-content: space-between;
  margin-bottom: 0.5rem;
}

.incident-details-dialog .comment-time {
  color: rgba(0, 0, 0, 0.6);
  font-size: 0.875rem;
}

.incident-details-dialog .comment-text {
  color: rgba(0, 0, 0, 0.87);
  line-height: 1.5;
}
</style>
